import { Pagination, PaginationResponse, api } from './api';

export enum MessageOptionType {
  Single = 'single',
  Multiple = 'multiple',
  Dropdown = 'dropdown',
  Tag = 'tag',
  Range = 'range',
  Date = 'date',
  Text = 'text',
  Confirm = 'confirm',
  Location = 'location',
}
export interface MessageOptions {
  labels: [string, number][];
  default1?: number;
  default2?: number;
  optionType?: MessageOptionType;
  placeholder?: string;
  disableOther?: boolean;
  // The matchmaker denormalizes a suggestion card onto the reply message.
  suggestion?: any;
}

export interface ConversationEntity {
  id: string;
  topic: string;
  topicTargetId: string;
  lastMessage: MessageEntity;
  lastMessageId: string;
  createdAt: string;
  updatedAt: string;
}

export interface MessageEntity {
  id: string;
  conversationId: string;
  text: string;
  options?: MessageOptions;
  senderId: string;
  topic: string;
  selectedOptions?: (number | string)[];
  otherString?: string;
  createdAt: string;
  updatedAt: string;
}

export interface UserConversationEntity {
  id: string;
  clearedAt: string;
  selfIsBlocked: boolean;
  title: string;
  userId: string;
  unread: number;
  conversation: ConversationEntity;
  conversationId: string;
  createdAt: string;
  updatedAt: string;
}

export interface FindUserConversationsDto extends Pagination {
  updatedAt__lt?: string;
}
export interface FindUserConversationsResponse extends PaginationResponse {
  data: UserConversationEntity[];
}

export interface FindMessagesDto extends Pagination {
  userConversationId: string;
  createdAt__lt?: string;
}

interface FindMessagesPagination extends PaginationResponse {
  data: MessageEntity[];
}
export interface FindMessagesResponse {
  userConversation: UserConversationEntity;
  messages: FindMessagesPagination;
}

export interface AdminFindUserMessagesResponse {
  userConversation: UserConversationEntity;
  messages: FindMessagesPagination;
}

export interface IMessageDto {
  id: string | number;
  text?: string;
  selectedOptions?: (string | number)[];
  otherString?: string;
}
export interface CreateMessageDto {
  userConversationId: string;
  message: IMessageDto;
}

export interface CreateMessageResponse {
  userConversation: UserConversationEntity;
  message: MessageEntity;
  // For AI conversations the matchmaker's reply is returned synchronously.
  aiResponse?: MessageEntity | null;
  // Admin impersonation only: the exact system prompt sent to the matchmaker.
  debugSystemPrompt?: string;
}

// Define a service using a base URL and expected endpoints
export const chatApi = api.injectEndpoints({
  endpoints: (builder) => ({
    fetchUserConversations: builder.query<
      FindUserConversationsResponse,
      FindUserConversationsDto
    >({
      query: (params) => ({
        url: '/user-conversations',
        method: 'GET',
        params,
      }),
    }),
    fetchMessages: builder.query<FindMessagesResponse, FindMessagesDto>({
      query: ({ userConversationId, ...params }) => ({
        url: `/user-conversations/${userConversationId}/messages`,
        method: 'GET',
        params,
      }),
    }),
    sendMessages: builder.mutation<CreateMessageResponse, CreateMessageDto>({
      query: ({ userConversationId, ...body }) => ({
        url: `/user-conversations/${userConversationId}/messages`,
        method: 'POST',
        body,
      }),
    }),
    testIntervew: builder.mutation<UserConversationEntity, number>({
      query: (id) => ({
        url: `/interviews/${id}/test`,
        method: 'POST',
      }),
    }),
    deleteUserConversation: builder.mutation<void, string>({
      query: (userConversationId) => ({
        url: `/user-conversations/${userConversationId}`,
        method: 'DELETE',
      }),
    }),
    adminFetchUserConversations: builder.query<
      FindUserConversationsResponse,
      { userId: string } & FindUserConversationsDto
    >({
      query: ({ userId, ...params }) => ({
        url: `/user-conversations/admin/user/${userId}`,
        method: 'GET',
        params,
      }),
    }),
    adminDeleteUserConversation: builder.mutation<void, string>({
      query: (userConversationId) => ({
        url: `/user-conversations/admin/${userConversationId}`,
        method: 'DELETE',
      }),
    }),
    adminFetchUserMessages: builder.query<
      AdminFindUserMessagesResponse,
      { userConversationId: string } & FindUserConversationsDto
    >({
      query: ({ userConversationId, ...params }) => ({
        url: `/user-conversations/admin/${userConversationId}/messages`,
        method: 'GET',
        params,
      }),
    }),
    adminDeleteMessage: builder.mutation<{ message: string }, string>({
      query: (messageId) => ({
        url: `/user-conversations/admin/messages/${messageId}`,
        method: 'DELETE',
      }),
    }),
  }),
});

export const {
  useLazyFetchUserConversationsQuery,
  useLazyFetchMessagesQuery,
  useSendMessagesMutation,
  useTestIntervewMutation,
  useDeleteUserConversationMutation,
  useLazyAdminFetchUserConversationsQuery,
  useAdminDeleteUserConversationMutation,
  useLazyAdminFetchUserMessagesQuery,
  useAdminDeleteMessageMutation,
} = chatApi;
